import { useEffect, useState } from "react";
import { fetchProtectedImage, revokeImageUrls } from "./imageUtil";
import { isUploadedMenuItemImage } from "./menuItemImage";

interface MenuItem {
  type: string;
  name: string;
  image?: string;
}

const isHttpUrl = (url?: string): url is string => {
  if (!url) {
    return false;
  }
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
};

// Links go dead - Discord CDN ones expire outright - and a dead one would
// render as a broken-image icon instead of the gradient tile, so only use a
// url the browser can actually load. The browser caches it, so the real
// <img> that follows doesn't fetch it again.
const loadsAsImage = (url: string) =>
  new Promise<boolean>((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => resolve(false), 5000);
    img.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };
    img.src = url;
  });

/**
 * Custom hook to fetch and manage protected menu item images
 * @param menuItems - Array of menu items with image URLs
 * @param loading - Loading state from the query
 * @returns Record of item type to blob URL
 */
export const useProtectedMenuImages = (
  menuItems: MenuItem[] | undefined,
  loading: boolean
): Record<string, string> => {
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (loading || !menuItems) return;

    let cancelled = false;
    const created: string[] = [];

    const loadImages = async () => {
      const newImageUrls: Record<string, string> = {};

      for (const item of menuItems) {
        // An upload is the normal case now that the bundled gifs are gone -
        // see isUploadedMenuItemImage for why it's safe to trust
        // unconditionally, unlike a manually pasted item.image.
        if (isUploadedMenuItemImage(item.image) && isHttpUrl(item.image)) {
          newImageUrls[item.type] = item.image;
          continue;
        }

        // Legacy: a gif that shipped with the app, keyed by item type. None
        // are bundled anymore, so this normally 404s and falls through.
        try {
          const blobUrl = await fetchProtectedImage(
            `menu-items/${item.type}.gif`
          );
          newImageUrls[item.type] = blobUrl;
          created.push(blobUrl);
        } catch (error) {
          // No bundled gif. Use the item's own image url if it has a working
          // one, the same fallback Discord embeds use; otherwise callers show
          // the gradient tile. It isn't a blob url, so it stays out of
          // `created` and never gets revoked.
          if (isHttpUrl(item.image) && (await loadsAsImage(item.image))) {
            newImageUrls[item.type] = item.image;
          } else {
            console.warn(`No menu image for "${item.type}":`, error);
          }
        }
      }

      if (cancelled) {
        revokeImageUrls(created);
        return;
      }
      setImageUrls(newImageUrls);
    };

    loadImages();

    // Revoke the urls this run created, not whatever was in state when it
    // started - that closure was stale and leaked every blob.
    return () => {
      cancelled = true;
      revokeImageUrls(created);
    };
  }, [menuItems, loading]);

  return imageUrls;
};
